﻿import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Autocomplete, TextField } from "@mui/material";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";

import { GeoMiningProductionTruckDTO } from "../../../DTOs/Geology/GeoMiningProductionTruckDTO";
import { DiggingLocationDTO } from "../../../DTOs/Production/DiggingLocationDTO";
import { LocationDTO } from "../../../DTOs/Production/LocationDTO";
import { TruckLoadDTO } from "../../../DTOs/Production/TruckLoadDTO";
import { TruckLoadsPagedResult } from "../../../DTOs/Production/TruckLoadsPagedResult";
import { PagedResult } from "../../../DTOs/Common/PagedResult";
import { detectionSourceLabel, isAutoDetected } from "../../../DTOs/Common/DetectionSource";
import { VerificationIndicator } from "../../../components/VerificationIndicator/VerificationIndicator";
import { queryClient } from "../../../queryClient";
import { diggingAjax } from "../../../ajax/DiggingAjax";
import { loadingsMutations } from "../../../offline/loadingsMutations";
import { geologyAjax } from "../../../ajax/GeologyAjax";
import DeleteButton from "../../../components/Buttons/DeleteButton";
import SaveButton from "../../../components/Buttons/SaveButton";
import { useGlobalModal } from "../../../components/Contexts/GlobalModalContext";
import { DataTable, TableRow } from "../../../components/DataTable/DataTable";
import { DataTableFilteredRequest, useDataTable } from "../../../components/DataTable/TableHook";
import { GroupedSelect } from "../../../components/GroupedSelect/GroupedSelect";
import { useOptions, useMaterialTypeGroupOptions } from "../../../components/Hooks/useOptions";
import useShiftLockInfo from "../../../components/Hooks/useShiftLockInfo";
import { useOnlineStatus } from "../../../components/Hooks/useOnlineStatus";
import { useScrollAndPulseRow } from "../../../components/Hooks/useScrollAndPulseRow";
import { useTimezone } from "../../../components/Providers/TimezoneProvider";
import { isCurrentShift } from "../../../utilities/helpers/shiftLockStateHelpers";
import {
  isHourSlotInFuture,
  hourIndexFromClockHour,
  clockHourFromHourIndex,
  resolveLiveShiftHourIndex,
} from "../../../utilities/helpers/shiftHourHelpers";
import MathInput from "../../../components/MathInput/MathInput";
import { RootState } from "../../../state/store";
import { TripleShift } from "../../../types/Shift";
import MoveLoadingRecordModal from "./MoveLoadingRecordModal";
import TrucksTableFooter from "./TrucksTableFooter";
import TrucksTableNoResultsContent from "./TrucksTableNoResultsContent";
import { shiftDurationHours, shiftStartHour } from "../../../offline/shiftBands";
import { isOnlineNow } from "../../../offline/connectivity";

interface InsertRowState {
  truckId: number;
  loads: number;
  loadsExpr?: string;
  materialTypeClassId: number;
  destinationId: number;
}

interface InsertRowProps {
  state: InsertRowState;
  setState: React.Dispatch<React.SetStateAction<InsertRowState>>;
  onInsert: () => Promise<boolean>;
  truckOptions: GeoMiningProductionTruckDTO[];
  isTruckOptionsLoading: boolean;
  materialTypes: { value: number; label: string; group: string; materialTypeId: number; isWaste: boolean }[];
  isMaterialTypesLoading: boolean;
  destinationOptions: LocationDTO[];
  allowedNonWasteLocationTypeIds: number[];
  allowedWasteLocationTypeIds: number[];
  isDestinationOptionsDataLoading: boolean;
  isShiftLocked: boolean;
}

type TruckLoadsTableMetaData = {
  currentHourTotalLoads: number;
  shiftTotalLoads: number;
};

const InsertInputsRow = ({
  state,
  setState,
  onInsert,
  truckOptions,
  isTruckOptionsLoading,
  materialTypes,
  isMaterialTypesLoading,
  destinationOptions,
  allowedNonWasteLocationTypeIds,
  allowedWasteLocationTypeIds,
  isDestinationOptionsDataLoading,
  isShiftLocked
}: InsertRowProps) => {
  const [isWorking, setIsWorking] = useState(false);

  const materialClassID = state.materialTypeClassId;
  const isSelectedMaterialClassWaste = materialTypes.find(m => m.value === materialClassID)?.isWaste;
  // TODO: this below should be moved to the backend to make sure we have a single source of truth
  const allowedTypeIds = isSelectedMaterialClassWaste ? allowedWasteLocationTypeIds : allowedNonWasteLocationTypeIds;
  const opts = destinationOptions.filter(d => allowedTypeIds.includes(d.locationTypeId));

  return (
    <ul className="flex gap-2 border-b border-theme-border py-1">
      <li className="basis-[20%] flex items-center justify-center text-[0.9rem] w-full">
        <Autocomplete
          value={truckOptions.find(o => o.plantId === state.truckId) ?? null}
          classes={{ root: "w-full" }}
          options={truckOptions}
          loading={isTruckOptionsLoading}
          disabled={isWorking || isTruckOptionsLoading || isShiftLocked}
          onChange={(_, opt) => setState(({ ...state, truckId: opt?.plantId ?? 0 }))}
          getOptionLabel={o => o.plantRef}
          renderInput={params => (
            <TextField
              {...params}
              placeholder="Select Truck"
              size="small"
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {isTruckOptionsLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
                      {params.slotProps.input.endAdornment}
                    </>
                  )
                }
              }}
            />
          )}
        />
      </li>

      <li className="basis-[10%] flex items-center justify-center text-[0.9rem] w-full">
        <MathInput
          value={state.loads}
          disabled={isWorking || isShiftLocked}
          onValueChange={(val) => setState({ ...state, loads: val })}
          step={1}
          className="w-full h-full"
        />
      </li>

      <li className="basis-[15%] flex items-center justify-center text-[0.9rem] w-full">
        <GroupedSelect
          options={materialTypes}
          value={state.materialTypeClassId}
          loading={isMaterialTypesLoading}
          disabled={isWorking || isMaterialTypesLoading || isShiftLocked}
          onChange={e => setState({ ...state, materialTypeClassId: e ?? 0 })}
          placeholder="Select Material"
        />
      </li>

      <li className="basis-[20%] flex items-center justify-center text-[0.9rem] w-full">
        <Autocomplete
          value={destinationOptions.find(o => o.id === state.destinationId) ?? null}
          classes={{ root: "w-full" }}
          options={opts}
          loading={isDestinationOptionsDataLoading}
          disabled={isWorking || isDestinationOptionsDataLoading || isShiftLocked}
          getOptionLabel={(o) => o.name}
          onChange={(_, opt) => setState({ ...state, destinationId: opt?.id ?? 0 })}
          renderInput={params => (
            <TextField
              {...params}
              placeholder="Select Destination"
              size="small"
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {isDestinationOptionsDataLoading ?
                        <FontAwesomeIcon icon={faSpinner} spin /> : null}
                      {params.slotProps.input.endAdornment}
                    </>
                  )
                }
              }}
            />
          )}
        />
      </li>

      <li className="basis-[15%] flex items-center justify-center text-[0.9rem] w-full">
        <span>You</span>
      </li>

      <li className="basis-[20%] flex items-center justify-center text-[0.9rem] w-full">
        <button
          disabled={isWorking || isShiftLocked}
          onClick={async () => {
            if (isWorking) return;
            setIsWorking(true);
            await onInsert();
            setIsWorking(false);
          }}
          className="bg-theme-info hover:bg-theme-info-hover disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed transition ease-in-out text-white font-bold py-2 px-4 rounded w-full whitespace-nowrap"
        >
          Add
        </button>
      </li>
    </ul>
  );
};

interface TrucksTableProps {
  selectedDate: DateTime;
  selectedShift: TripleShift;
  selectedLocation: DiggingLocationDTO;
  availableLocations: DiggingLocationDTO[];
  destinationOptions: LocationDTO[];
  allowedNonWasteLocationTypeIds: number[];
  allowedWasteLocationTypeIds: number[];
  isDestinationOptionsDataLoading: boolean;
  registerUpdateTrucksTableRowFn: (fn: () => void) => void;
  initialHourIndex?: number | null;
  initialLoadingId?: number | null;
  onNavigationConsumed?: () => void;
}

export const TrucksTable: React.FC<TrucksTableProps> = ({
  selectedDate,
  selectedShift,
  selectedLocation,
  availableLocations,
  destinationOptions,
  allowedNonWasteLocationTypeIds,
  allowedWasteLocationTypeIds,
  isDestinationOptionsDataLoading,
  registerUpdateTrucksTableRowFn,
  initialHourIndex,
  initialLoadingId,
  onNavigationConsumed
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useGlobalModal();
  const isShiftLocked = useSelector((s: RootState) => s.shift.isLocked);
  const isOnline = useOnlineStatus();

  const [lockedTabKey, setLockedTabKey] = useState<string | null>("loads");
  const navConsumedRef = useRef(false);

  // Note: from the configured shift bands, not a DAY/NIGHT ternary. This screen hardcoded 7 and 19
  // against bands of 06:00 and 18:00, so every hour label was an hour out and the live-hour marker
  // pointed at the wrong button.
  const shiftStartClockHour = shiftStartHour(selectedShift);
  const shiftHourCount = shiftDurationHours(selectedShift);
  const indexFromClockHour = useCallback(
    (clockHour: number) => hourIndexFromClockHour(clockHour, shiftStartClockHour),
    [shiftStartClockHour]
  );
  const clockHourFromIndex = useCallback(
    (idx: number) => clockHourFromHourIndex(idx, shiftStartClockHour),
    [shiftStartClockHour]
  );

  const hours = useMemo(() => {
    const startClock = selectedLocation.startTime.toLocal().hour;
    const startIndex = indexFromClockHour(startClock);
    const indices: number[] = [];
    for (let i = startIndex; i <= shiftHourCount; i++) indices.push(i);
    return indices.map(i => ({
      index: i,
      label: `${clockHourFromIndex(i)} - ${(clockHourFromIndex(i) + 1) % 24}`
    }));
  }, [selectedLocation.startTime, indexFromClockHour, clockHourFromIndex, shiftHourCount]);

  const { shiftLockState } = useShiftLockInfo(selectedDate, selectedShift);
  const { timezone } = useTimezone();
  const isLatestShift = useMemo(
    () => isCurrentShift(selectedDate, selectedShift === "NIGHT" ? "N" : "D", shiftLockState),
    [selectedDate, selectedShift, shiftLockState]
  );

  const [nowTick, setNowTick] = useState(() => DateTime.now());
  useEffect(() => {
    if (!isLatestShift) return;
    const id = setInterval(() => setNowTick(DateTime.now()), 30_000);
    return () => clearInterval(id);
  }, [isLatestShift]);

  const liveHourIndex = useMemo(
    () => resolveLiveShiftHourIndex(
      nowTick.setZone(timezone ?? "UTC").hour, shiftStartClockHour, shiftHourCount, isLatestShift),
    [nowTick, timezone, shiftStartClockHour, shiftHourCount, isLatestShift]
  );

  const [selectedHourIndex, setSelectedHourIndex] = useState<number>(() => {
    if (initialHourIndex != null) return Math.min(Math.max(initialHourIndex, 1), shiftHourCount);
    const start = indexFromClockHour(selectedLocation.startTime.toLocal().hour);
    return Math.min(Math.max(start, 1), shiftHourCount);
  });

  const [highlightedRowId, setHighlightedRowId] = useState<number | null>(null);
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);

  const { options: materialTypes, isLoading: isMaterialTypesLoading } = useMaterialTypeGroupOptions();
  const { options: truckOptionsRaw, isLoading: isTruckOptionsLoading } = useOptions<GeoMiningProductionTruckDTO, GeoMiningProductionTruckDTO>(
    ['geology', 'productionTrucks', selectedDate.toISODate()],
    () => geologyAjax.getProductionTruckingTrucks(selectedDate),
    x => x,
  );

  const truckOptions = useMemo(
    () => [...truckOptionsRaw].sort((a, b) => a.plantRef.localeCompare(b.plantRef, undefined, { numeric: true, sensitivity: 'base' })),
    [truckOptionsRaw]
  );

  const rowVersionByLoadingIdRef = useRef<Map<number, string | undefined>>(new Map());

  const buildTruckLoadDtoFromTableRow = useCallback((r: TableRow<number>): TruckLoadDTO => {
    return new TruckLoadDTO({
      loadingId: r.id,
      diggingPatternId: selectedLocation.diggingPatternId,
      destinationId: r.values.find(v => v.name === "Destination")?.value,
      truckId: r.values.find(v => v.name === "Truck")?.value,
      loads: r.values.find(v => v.name === "Loads")?.value,
      hour: selectedHourIndex,
      materialTypeClassId: r.values.find(v => v.name === "Material Type Class")?.value,
      rowVersion: rowVersionByLoadingIdRef.current.get(r.id)
    });
  }, [selectedLocation.diggingPatternId, selectedHourIndex]);

  const autoSaveDisplayedRow = useCallback(async (r: TableRow<number>) => {
    try {
      return await handleUpdateRow(buildTruckLoadDtoFromTableRow(r));
    } catch (error) {
      console.error('Saving a truck load failed', error);
      enqueueSnackbar("That load could not be saved. Check the value and try again.", { variant: "error", autoHideDuration: 5000 });
      return false;
    }
  }, [buildTruckLoadDtoFromTableRow, enqueueSnackbar]);

  const handleMoveLoadingRecord = useCallback(
    async (loadingId: number, newDiggingPatternId: number) => {
      const response = await diggingAjax.moveLoadingRecord(loadingId, newDiggingPatternId);

      if (!response.success) {
        response.errors?.forEach((error, index) =>
          enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 5000 + index * 1500 })
        );
        return false;
      }

      return true;
    },
    [enqueueSnackbar]
  );

  const [insertState, setInsertState] = useState<InsertRowState>({
    truckId: 0,
    loads: 0,
    materialTypeClassId: 0,
    destinationId: 0
  });

  const getRows = useCallback((request: DataTableFilteredRequest) => diggingAjax.getTruckLoads(
    selectedLocation.diggingPatternId,
    selectedHourIndex,
    { ...request, unverifiedOnly }),
    [selectedLocation.diggingPatternId, selectedHourIndex, unverifiedOnly]
  );

  const translateRows = useCallback((row: TruckLoadDTO): TableRow => {
    rowVersionByLoadingIdRef.current.set(row.loadingId, row.rowVersion);
    return {
      id: row.loadingId,
      rowTailwindClasses: `${highlightedRowId === row.loadingId ? "glow-once" : ""}`,
      values: [
        {
          name: "Truck",
          editable: false,
          sortable: true,
          type: "select",
          tailwindClasses: "basis-[18%]",
          value: row.truckId,
          tabLockKey: "truck",
          render: (_tableValue, tableRow, helpers) => (
            <Autocomplete
              disabled={isShiftLocked}
              value={truckOptions.find(t => t.plantId === helpers.draft.get()) ?? null}
              classes={{ root: "w-full" }}
              options={truckOptions}
              autoHighlight
              onChange={async (_, opt) => {
                const newValue = opt?.plantId ?? 0;
                helpers.draft.set(newValue);
                const updatedRow: TableRow<number> = {
                  ...tableRow,
                  values: tableRow.values.map(v =>
                    v.name === "Truck" ? { ...v, value: newValue } : v
                  )
                };
                await autoSaveDisplayedRow(updatedRow);
              }}
              getOptionLabel={o => o.plantRef}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Select Truck"
                  size="small"
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps.input,
                      inputRef: helpers.tabLock.registerInputRef,
                      onKeyDown: helpers.tabLock.handleLockedTabbing,
                      endAdornment: (
                        <>
                          {isTruckOptionsLoading ?
                            <FontAwesomeIcon icon={faSpinner} spin /> : null}
                          {params.slotProps.input.endAdornment}
                        </>
                      )
                    }
                  }}
                />
              )}
            />
          )
        },
        {
          name: "Loads",
          editable: true,
          sortable: true,
          type: "custom",
          tailwindClasses: "basis-[10%] py-1",
          value: row.loads,
          tabLockKey: "loads",
          render: (value, r, helpers) => (
            <MathInput
              disabled={isShiftLocked}
              value={helpers.draft.get()}
              step={1}
              inputRef={helpers.tabLock.registerInputRef}
              onKeyDown={helpers.tabLock.handleLockedTabbing}
              onValueChange={(val) => {
                if (val == null || Number.isNaN(val)) return;
                helpers.draft.set(val);
              }}
              onBlur={async (finalValue) => {
                if (finalValue == null || Number.isNaN(finalValue) || finalValue === value.value) return;

                const updatedRow: TableRow<number> = {
                  ...r,
                  values: r.values.map(v =>
                    v.name === "Loads" ? { ...v, value: finalValue } : v
                  )
                };

                await autoSaveDisplayedRow(updatedRow);
              }}
            />
          ),
        },
        {
          name: "Material Type Class",
          editable: true,
          sortable: true,
          type: "integer",
          tailwindClasses: "basis-[15%] py-1",
          value: row.materialTypeClassId,
          tabLockKey: "material",
          render: (_value, r, helpers) => (
            <GroupedSelect
              disabled={isShiftLocked}
              options={materialTypes}
              value={helpers.draft.get()}
              onChange={async newValue => {
                helpers.draft.set(newValue);

                const updatedRow: TableRow<number> = {
                  ...r,
                  values: r.values.map(v =>
                    v.name === "Material Type Class" ? { ...v, value: newValue } : v
                  )
                };

                await autoSaveDisplayedRow(updatedRow);
              }}
              placeholder="Select Material"
              loading={isMaterialTypesLoading}
              inputRef={helpers.tabLock.registerInputRef}
              onKeyDown={helpers.tabLock.handleLockedTabbing}
            />
          )
        },
        {
          name: "Destination",
          editable: true,
          sortable: true,
          type: "select",
          tailwindClasses: "basis-[18%]",
          value: row.destinationId,
          tabLockKey: "destination",
          render: (_value, r, helpers) => {
            const materialClassID =
              (r?.values.find(v => v.name === "Material Type Class")?.value ??
                row.materialTypeClassId) as number | undefined;
            const isWaste = materialTypes.find(m => m.value === materialClassID)?.isWaste;
            const opts = destinationOptions.filter(d => (isWaste ? allowedWasteLocationTypeIds : allowedNonWasteLocationTypeIds).includes(d.locationTypeId));

            return (
              <Autocomplete
                disabled={isShiftLocked}
                value={opts.find(o => o.id === helpers.draft.get()) ?? null}
                classes={{ root: "w-full" }}
                options={opts}
                loading={isDestinationOptionsDataLoading}
                getOptionLabel={(o) => o.name}
                onChange={async (_, opt) => {
                  const newValue = opt?.id ?? 0;
                  helpers.draft.set(newValue);

                  const updatedRow: TableRow<number> = {
                    ...r,
                    values: r.values.map(v =>
                      v.name === "Destination" ? { ...v, value: newValue } : v
                    )
                  };

                  await autoSaveDisplayedRow(updatedRow);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder="Select Destination"
                    size="small"
                    slotProps={{
                      ...params.slotProps,
                      input: {
                        ...params.slotProps.input,
                        inputRef: helpers.tabLock.registerInputRef,
                        onKeyDown: helpers.tabLock.handleLockedTabbing,
                        endAdornment: <>{params.slotProps.input.endAdornment}</>
                      }
                    }}
                  />
                )}
              />
            );
          }
        },
        {
          name: "Added By",
          editable: false,
          sortable: true,
          type: "string",
          tailwindClasses: "basis-[11%]",
          value: row.addedBy
        },
        {
          name: "Source",
          editable: false,
          type: "custom",
          tailwindClasses: "basis-[8%]",
          value: row.isVerified === false ? "Needs checking" : "",
          render: () => (
            <div className="flex items-center justify-center gap-1.5 w-full">
              <div className="text-theme-text-muted text-xs">
                {detectionSourceLabel(row.detectionSource)}
              </div>
              {/* Note: read-only here. There is no per-row accept endpoint on this screen - the trucks
                  table clears a whole hour at once and Stoppages clears by editing - so the mark reports
                  state without pretending to be a control. */}
              {isAutoDetected(row.detectionSource) && (
                <VerificationIndicator isVerified={row.isVerified !== false} subject="truck load" />
              )}
            </div>
          )
        },
        {
          name: "Actions",
          type: "custom",
          tailwindClasses: "basis-[20%]",
          value: "custom",
          render: () => (
            <div className="flex justify-center align-items-center gap-1 w-full">
              <DeleteButton
                disabled={isShiftLocked}
                handleDelete={() => handleDeleteRow(row.loadingId, row.rowVersion)}
              />
              <button
                disabled={isShiftLocked}
                onClick={async () => {
                  const otherLocations = availableLocations.filter(
                    loc => loc.diggingPatternId !== selectedLocation.diggingPatternId
                  );

                  showModal({
                    content: () => (
                      <MoveLoadingRecordModal
                        availableLocations={otherLocations}
                        currentLocation={selectedLocation}
                        onMove={async (newDiggingPatternId) => {
                          const success = await handleMoveLoadingRecord(row.loadingId, newDiggingPatternId);
                          if (success) {
                            invalidate();
                            enqueueSnackbar("Loading record moved successfully", { variant: "success", autoHideDuration: 3000 });
                          }
                          return success;
                        }}
                      />
                    )
                  });
                }}
                className="bg-theme-info hover:bg-theme-info-hover cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 transition ease-in-out text-white font-bold py-2 px-4 whitespace-nowrap"
              >
                Move
              </button>
            </div>
          )
        }
      ]
    };
  }, [
    highlightedRowId,
    isShiftLocked,
    truckOptions,
    autoSaveDisplayedRow,
    isTruckOptionsLoading,
    materialTypes,
    isMaterialTypesLoading,
    destinationOptions,
    isDestinationOptionsDataLoading,
    allowedWasteLocationTypeIds,
    allowedNonWasteLocationTypeIds,
    availableLocations,
    showModal,
    selectedLocation,
    handleMoveLoadingRecord,
    enqueueSnackbar
  ])

  const {
    displayData,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    sortField,
    sortDirection,
    setPage,
    appendRowSource,
    updateRowSource,
    removeRowSource,
    invalidate,
    tableMetaData,
  } = useDataTable<TruckLoadDTO, number, TruckLoadsTableMetaData, TruckLoadsPagedResult>({
    fetchFn: getRows,
    translateFn: translateRows,
    queryKey: ['chatree', 'truckLoads', selectedLocation.diggingPatternId, selectedHourIndex, unverifiedOnly],
    initialPage: 1,
    pageSize: 5,
    selectMeta: (result) => ({
      currentHourTotalLoads: result.currentHourTotalLoads,
      shiftTotalLoads: result.shiftTotalLoads
    })
  });

  const handleInsertRow = useCallback(async () => {
    let isValid = true;
    if (insertState.truckId === 0) {
      enqueueSnackbar("Truck must be selected", { variant: "error", autoHideDuration: 3000 });
      isValid = false;
    }
    if (insertState.loads == null || insertState.loads < 0) {
      enqueueSnackbar("Loads can't be less than 0", { variant: "error", autoHideDuration: 3000 });
      isValid = false;
    }
    if (insertState.destinationId === 0) {
      enqueueSnackbar("Destination must be selected", { variant: "error", autoHideDuration: 3000 });
      isValid = false;
    }
    if (!insertState.materialTypeClassId) {
      enqueueSnackbar("Material type must be selected", { variant: "error", autoHideDuration: 3000 });
      isValid = false;
    }
    if (!isValid) return false;

    const payload: TruckLoadDTO = {
      loadingId: 0,
      diggingPatternId: selectedLocation.diggingPatternId,
      destinationId: insertState.destinationId,
      truckId: insertState.truckId,
      loads: insertState.loads,
      hour: selectedHourIndex,
      materialTypeClassId: insertState.materialTypeClassId
    };

    const response = await loadingsMutations.insertTruckLoad(payload);
    if (!response.success || !response.data || !response.data.truckLoad) {
      response.errors?.forEach((error, index) =>
        enqueueSnackbar("Error: " + error.description, { variant: "error", autoHideDuration: 5000 + index * 1500 })
      );
      return false;
    }

    setInsertState(prev => ({
      truckId: prev.truckId,
      loads: 0,
      materialTypeClassId: prev.materialTypeClassId,
      destinationId: prev.destinationId
    }));
    enqueueSnackbar("Truck load added successfully", { variant: "success", autoHideDuration: 3000 });
    appendRowSource(response.data!.truckLoad!, {
      currentHourTotalLoads: response.data!.currentHourTotalLoads,
      shiftTotalLoads: response.data!.shiftTotalLoads,
    });

    return true;
  }, [insertState.truckId, insertState.loads, insertState.destinationId, insertState.materialTypeClassId, selectedLocation.diggingPatternId, selectedHourIndex, enqueueSnackbar, appendRowSource]);

  // Note: the counterpart to the Needs Checking filter. Editing a row was the only way to clear its flag,
  // so a load that was already correct could not be confirmed at all - you had to nudge a value and put it
  // back. Fine for the handful a person types, useless against telemetry, which produces hundreds.
  const handleVerifyAll = useCallback(async () => {
    if (!isOnlineNow()) {
      enqueueSnackbar("Verify All is not available offline", { variant: "warning", autoHideDuration: 4000 });
      return false;
    }

    const resp = await diggingAjax.verifyTruckLoads(
      selectedLocation.diggingPatternId,
      selectedHourIndex
    );
    if (!resp.success) {
      resp.errors?.forEach((e, i) =>
        enqueueSnackbar(e.description, { variant: "error", autoHideDuration: 4000 + i * 1000 })
      );
      return false;
    }

    const verifiedCount = resp.data ?? 0;
    enqueueSnackbar(
      verifiedCount === 0
        ? "Nothing needed checking in this hour"
        : `${verifiedCount} truck load${verifiedCount === 1 ? "" : "s"} marked as checked`,
      { variant: "success", autoHideDuration: 3000 }
    );
    invalidate();
    return true;
  }, [selectedLocation.diggingPatternId, selectedHourIndex, enqueueSnackbar, invalidate]);

  // Note: previous is the row before the edit, forwarded so the Production list's cached loads move by
  // the difference rather than being missed.
  const handleUpdateRow = useCallback(
    async (row: TruckLoadDTO, previous?: TruckLoadDTO) => {
      let isValid = true;
      if (row.truckId === 0) {
        isValid = false;
        enqueueSnackbar("Truck must be selected", { variant: "error", autoHideDuration: 3000 });
      }
      if (!row.materialTypeClassId) {
        isValid = false;
        enqueueSnackbar("Material type must be selected", { variant: "error", autoHideDuration: 3000 });
      }
      if (row.destinationId === 0) {
        isValid = false;
        enqueueSnackbar("Destination must be selected", { variant: "error", autoHideDuration: 3000 });
      }
      if (row.loads == null || row.loads < 0) {
        isValid = false;
        enqueueSnackbar("Loads cannot less than 0", { variant: "error", autoHideDuration: 3000 });
      }
      if (!isValid) return false;

      const resp = await loadingsMutations.updateTruckLoad(row, previous);
      if (!resp.success || !resp.data || !resp.data.truckLoad) {
        resp.errors?.forEach((e, i) =>
          enqueueSnackbar(e.description, { variant: "error", autoHideDuration: 4000 + i * 1000 })
        );

        invalidate();
        return false;
      }

      enqueueSnackbar("Truck load updated successfully", { variant: "success", autoHideDuration: 1500 });
      const updated = resp.data!.truckLoad!;
      updateRowSource(r => r.loadingId === updated.loadingId, updated, {
        currentHourTotalLoads: resp.data!.currentHourTotalLoads,
        shiftTotalLoads: resp.data!.shiftTotalLoads,
      });

      return true;
    },
    [updateRowSource, enqueueSnackbar, invalidate]
  );

  const handleDeleteRow = useCallback(
    async (loadingId: number, rowVersion?: string, removed?: TruckLoadDTO) => {
      const response = await loadingsMutations.deleteTruckLoad(loadingId, rowVersion, removed);
      if (!response.success || !response.data) {
        response.errors?.forEach((error, index) =>
          enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 5000 + index * 1500 })
        );
        return false;
      }

      removeRowSource(r => r.loadingId === loadingId, {
        currentHourTotalLoads: response.data!.currentHourTotalLoads,
        shiftTotalLoads: response.data!.shiftTotalLoads,
      });
      enqueueSnackbar("Truck load deleted successfully", { variant: "success", autoHideDuration: 3000 });
      return true;
    },
    [removeRowSource, enqueueSnackbar]
  );

  const { pulseRowKey } = useScrollAndPulseRow(
    initialLoadingId != null ? String(initialLoadingId) : null,
    !isLoading,
    useCallback(() => onNavigationConsumed?.(), [onNavigationConsumed]),
    { scrollDelayMs: 0, pulseDelayMs: 500, pulseDurationMs: 2800, expireMs: 500 + 2800 }
  );

  useEffect(() => {
    setHighlightedRowId(pulseRowKey != null ? Number(pulseRowKey) : null);
  }, [pulseRowKey]);

  useEffect(() => {
    if (navConsumedRef.current) return;

    const hasHighlight = initialLoadingId != null;
    const hasHour = initialHourIndex != null;

    if (!hasHour || hasHighlight) return;
    if (isLoading) return;
    if (selectedHourIndex !== initialHourIndex) return;

    navConsumedRef.current = true;
    onNavigationConsumed?.();
  }, [initialHourIndex, initialLoadingId, selectedHourIndex, isLoading, onNavigationConsumed]);

  useEffect(() => {
    registerUpdateTrucksTableRowFn(() => invalidate());

    return () => registerUpdateTrucksTableRowFn(() => { });
  }, [invalidate, registerUpdateTrucksTableRowFn]);

  const offlineShiftTotal = useMemo(() => {
    if (isOnline) return undefined

    let total = 0
    for (const [, page] of queryClient.getQueriesData<PagedResult<TruckLoadDTO>>({
      queryKey: ['dataTable', 'chatree', 'truckLoads', selectedLocation.diggingPatternId],
    })) {
      total += (page?.data ?? []).reduce((sum, row) => sum + (Number(row?.loads) || 0), 0)
    }
    return total
  }, [isOnline, selectedLocation.diggingPatternId, displayData])

  const currentHourLabel = useMemo(() => {
    const start = clockHourFromIndex(selectedHourIndex);
    const end = (start + 1) % 24;
    return `${start}-${end}`;
  }, [selectedHourIndex, clockHourFromIndex]);

  return (
    <div className="flex flex-col w-full bg-theme-surface-2 border border-theme-table-border p-4 text-theme-text">
      <h2 className="text-2xl font-bold mb-4">
        Production - <span className="underline">{selectedLocation.blastMasterId ?? selectedLocation.patternBlastId}</span>
      </h2>

      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center space-x-2 overflow-auto fade-up">
          {hours.map(h => {
            const isLive = h.index === liveHourIndex;
            const isFuture = isHourSlotInFuture(h.index, liveHourIndex ?? -1);
            return (
              <button
                key={h.index}
                disabled={isFuture}
                title={isFuture ? "This hour hasn't started yet" : isLive ? "Current hour" : undefined}
                onClick={() => setSelectedHourIndex(h.index)}
                className={`flex items-center gap-1.5 px-2 py-1 rounded border border-theme-info whitespace-nowrap cursor-pointer transition disabled:opacity-40 disabled:cursor-not-allowed ${h.index === selectedHourIndex ? "bg-theme-surface hover:bg-theme-surface-2 text-theme-info" : "bg-theme-info hover:bg-theme-info-hover text-white"
                  }`}
              >
                {isLive && (
                  <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
                    <span className="absolute inline-flex h-full w-full rounded-full bg-theme-success opacity-75 animate-slow-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-theme-success" />
                  </span>
                )}
                {h.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-4 shrink-0">
          <label className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <input
              type="checkbox"
              checked={unverifiedOnly}
              className="w-6 h-6 border-theme-border rounded-md focus:ring-blue-500 focus:ring-2"
              title="Show only loads the telemetry recorded that nobody has checked yet"
              onChange={(e) => setUnverifiedOnly(e.target.checked)}
            />
            Needs Checking
          </label>

          <SaveButton
            buttonLabel={"Verify All"}
            confirmMessage={"Mark every unchecked truck load in this hour as checked?"}
            disabled={isShiftLocked || !isOnline}
            handleSave={handleVerifyAll}
          />

          <SaveButton
            buttonLabel={"Copy Previous Hour"}
            disabled={isShiftLocked || !isOnline}
            handleSave={async () => {
              if (!isOnlineNow()) {
                enqueueSnackbar("Copy Previous Hour needs a connection; it is not available offline.", { variant: "warning", autoHideDuration: 5000 });
                return false;
              }
              const newTruckLoads = await diggingAjax.copyPreviousHourLoads(
                selectedLocation.diggingPatternId,
                selectedHourIndex
              );

              if (!newTruckLoads.success || !newTruckLoads.data) {
                newTruckLoads.errors?.forEach((error, index) =>
                  enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 5000 + index * 1500 })
                );
                return false;
              }

              if (newTruckLoads.data.length === 0) {
                enqueueSnackbar("No truck loads to copy", { variant: "success", autoHideDuration: 3000 });
                return true;
              }

              enqueueSnackbar("Truck loads copied successfully", { variant: "success", autoHideDuration: 3000 });
              newTruckLoads.data.forEach(l => appendRowSource(l));

              invalidate();
              return true;
            }}
          />
        </div>
      </div>

      <DataTable
        data={displayData}
        isLoading={isLoading}
        isBackgroundFetching={isBackgroundFetching}
        error={isOnline ? error : null}
        pageNumber={pageNumber}
        setPage={setPage}
        sortField={sortField}
        sortDirection={sortDirection}
        minTableWidthPixels={1320}
        minTableHeightCssValue="320px"
        maxTableHeightCssValue="360px"
        disableContainer={true}
        enableTabLocking
        tabLockOptions={[
          { key: "truck", label: "Truck" },
          { key: "loads", label: "Loads" },
          { key: "material", label: "Material" },
          { key: "destination", label: "Destination" }
        ]}
        lockedTabKey={lockedTabKey}
        setLockedTabKey={setLockedTabKey}
        noResultsContent={<TrucksTableNoResultsContent />}
        tableFooterContent={<TrucksTableFooter
          shiftTotal={isOnline ? tableMetaData?.shiftTotalLoads : offlineShiftTotal}
          currentHourLabel={currentHourLabel}
          // Note: while the Needs Checking filter is on the visible rows are only part of the hour, so
          // summing them would understate it. The server's figure counts every load either way.
          currentHourSum={unverifiedOnly
            ? tableMetaData?.currentHourTotalLoads ?? 0
            : displayData.reduce((prev, curr) => prev + curr.values.find(x => x.name === "Loads")?.value, 0)}
          isLoading={isLoading}
        />}
        insertRowRenderer={() => (
          <InsertInputsRow
            isShiftLocked={isShiftLocked}
            state={insertState}
            setState={setInsertState}
            onInsert={handleInsertRow}
            truckOptions={truckOptions}
            isTruckOptionsLoading={isTruckOptionsLoading}
            materialTypes={materialTypes}
            isMaterialTypesLoading={isMaterialTypesLoading}
            destinationOptions={destinationOptions}
            allowedNonWasteLocationTypeIds={allowedNonWasteLocationTypeIds}
            allowedWasteLocationTypeIds={allowedWasteLocationTypeIds}
            isDestinationOptionsDataLoading={isDestinationOptionsDataLoading}
          />
        )}
        overrideHeaderCells={[
          { name: "Truck", tailwindClasses: "basis-[18%]", tabLockKey: "truck" },
          { name: "Loads", tailwindClasses: "basis-[10%]", tabLockKey: "loads" },
          { name: "Material type", tailwindClasses: "basis-[15%]", tabLockKey: "material" },
          { name: "Destination", tailwindClasses: "basis-[18%]", tabLockKey: "destination" },
          { name: "Added by", tailwindClasses: "basis-[11%]" },
          { name: "Source", tailwindClasses: "basis-[8%]" },
          { name: "Actions", tailwindClasses: "basis-[20%]" }
        ]}
      />
    </div>
  );
};
